import React, { useCallback, useMemo } from 'react';
import type { AthleteProps, BestAvailableOrderProps, EventOrderStatProps, EventProps, LeagueProps, MarketProps, MarketSideOptionProps, OrderProps, TradeProps } from "../../../types"
import MarketButton from '../MarketButton';
import { Icons, LinearDiagnal } from '../../../Components';
import Colors from '../../../constants/colors';
import { MarketButtonHelpers } from '../MarketButton/api';
import { MarketComponentHelpers } from '../../api';
import { AthleteImage } from '../../../Components/Jerseys';
import { Button, Text, View } from '../../../Components/Themed';
import { useColors } from '../../../constants/useColors';

type AthleteMarketProps = {
    event:EventProps,
    event_type:'team'|'tournament'|'match',
    athlete:AthleteProps,
    hide_stat_banner?:boolean,
    disabled_markets?:string[],
    width?:number,
    market:MarketProps,
    onSetMarket?:(market_id:string, side_type?:string, side_id?:string) => void,
    float?:boolean,
    direction?:'horizontal'|'vertical',
    style?:any,
    highlight_router_orders?:boolean
    image_size?:number,
    league?:LeagueProps,
    hide_image?:boolean,
    show_event?:boolean,
    hide_liquidity?:boolean,
    latest_trades:TradeProps[],
    best_available_orders:BestAvailableOrderProps[],
    event_order_stats:EventOrderStatProps[],
    default_price_view:'last_trade'|'best_available',
    onOrder:(order:OrderProps) => void,
    show_grades?:boolean,
    onView:(data:{event_id:string, event_type:string, market_id:string, side_type:'athlete', side_id:string}) => void,
    onTradeLongPress:(trade:TradeProps) => void
}

const AthleteMarket = ({ event, show_event, disabled_markets, highlight_router_orders, float, image_size, hide_image, direction, league, hide_liquidity, style, hide_stat_banner, athlete, market, event_order_stats, default_price_view, latest_trades, show_grades, best_available_orders, onOrder, onView, onTradeLongPress, onSetMarket }:AthleteMarketProps) => {
    const C = useColors();

    const { liquidity, open_order_count } = useMemo(() => MarketComponentHelpers.getLiqudity(event_order_stats), [JSON.stringify(event_order_stats)])
    const is_locked = useMemo(() => MarketComponentHelpers.isEventMarketLocked(event, market.market_id),[JSON.stringify(event.supported_markets)])
    
    const renderSides = useCallback((data:{ item:MarketSideOptionProps, index:number, market:MarketProps }) => {
        const ba_order = best_available_orders.find(o => o.market_id == market.market_id && o.side_type == 'athlete' && o.side_id == athlete.athlete_id && o.side == data.item.side && o.show);
        const side_details = MarketComponentHelpers.getTeamEventSideIds(event, market, data.item, athlete, undefined);
        if(!side_details){ return <></> }
        let trade = MarketButtonHelpers.getVisibleTrade(default_price_view, data.market, data.item, latest_trades, best_available_orders, side_details.side_id, side_details.reversed_side_id); 
        if(!trade){
            trade = MarketButtonHelpers.getTradeFromMarket(market, event.event_id, 'team', data.item.side, data.item.id_source, side_details.side_id, side_details.reversed_side_id);
        }
        let disabled = false
        if(disabled_markets && disabled_markets.includes(data.market.market_id.toString())){ disabled = true }
        return (
            <View key={`${event.event_id}_${athlete.athlete_id}_${market.market_id}_${data.item.side}`} style={{ margin:2, height:50, minWidth:65, flexGrow:1 }}>
                <MarketButton
                    side_option={data.item}
                    market={data.market}
                    trade={trade}
                    show_grades={show_grades}
                    order={ba_order}
                    highlight_router_orders={highlight_router_orders}
                    locked={is_locked || disabled}
                    onLongPress={onTradeLongPress}
                    onPress={(order) => {
                        const order_title = MarketComponentHelpers.getOrderTitleForTeamEvent(order, market, event, athlete);
                        if(!order_title){ return alert('Unable to process order') }
                        onOrder({ ...order, title: order_title, league_id: event?.league_id })
                    }}
                />
            </View>
        )
    },[athlete.athlete_id, is_locked, market.market_id, JSON.stringify(event_order_stats), JSON.stringify(best_available_orders), JSON.stringify(disabled_markets), default_price_view])
    return (
        <View float={float} style={[{ borderRadius:8 }, style]}>
            {!hide_stat_banner ?
             <LinearDiagnal 
                label={market.stat_label ?? market.type}
                label_size={12}
                style={{ borderTopLeftRadius:8, padding:5  }}
                left_color={Colors.incentive.gold}
                right_color={'#ebd197'}
            />
            :<></>}
            {show_event ?
            <View type='header' style={{ padding:5 }}>
                <Text size={12} theme='description' textAlign='center' weight='semibold'>{event.event_title}</Text>
            </View>
            :<></>}
            <View transparent style={{ flexDirection:direction == 'horizontal' ? 'row' : 'column', flexGrow:1 }}>
                <View type='body' style={{ flexDirection:direction == 'horizontal' ? 'row' : 'column' , flexGrow:2, padding:0, justifyContent:'center', alignItems:'center' }}>
                    {!hide_image ?
                    <AthleteImage
                        athlete={athlete}
                        league={league}
                        size={image_size ?? 50}
                        number_size={12}
                    />
                    :<></>}
                    <View style={{ flexGrow:1, padding:10, paddingBottom:0 }}>
                        <Text weight='bold' style={{ maxWidth:100 }} theme='h2' textAlign={direction == 'horizontal' ?'left' :'center'}>{athlete.first_name} {athlete.last_name}</Text>
                        <Text style={{ marginTop:3 }} theme='description' textAlign={direction == 'horizontal' ?'left' :'center'} size={12}>{athlete.team?`${athlete.team.abbr}`:''} ({athlete.position})</Text>
                    </View>
                </View>
                <View transparent style={{ flexGrow:1, justifyContent:direction=='vertical'?'center':'flex-end', padding:10 }}>
                    <View nativeID='market_sides' style={{ flexDirection:'row', flexGrow:1, alignItems:'center' }}>
                        {market.side_options.sort((a,b) => {
                            let a_t = a.side == market.trade_side ? 1 : 0
                            let b_t = b.side == market.trade_side ? 1 : 0
                            return a_t - b_t
                        }).map((so, index) => {
                            return renderSides({ item:so, index, market })
                        })}
                        
                    </View>
                    {onSetMarket ?
                    <Button
                        title='SET'
                        type='success'
                        style={{ margin:2, padding:8 }}
                        onPress={() => onSetMarket(market.market_id, 'athlete', athlete.athlete_id)}
                    />
                    :<></>}
                </View>

            </View>
            {!hide_liquidity ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', borderBottomLeftRadius:8, borderBottomRightRadius:8 }}>
                <Button transparent style={{ flexGrow:1, flexDirection:'row', padding:10 }} onPress={() => {
                    onView({
                        event_id: event.event_id, 
                        event_type: 'team', 
                        market_id: market.market_id, 
                        side_type: 'athlete', 
                        side_id: athlete.athlete_id
                    });
                }}>
                    <View float style={{ height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                        <Icons.LiquidityIcon color={C.text.h1} size={30}/>
                    </View>
                    <View transparent style={{ marginLeft:10 }}>
                        <Text theme='description'>Liquidity</Text>
                        <Text size={12} style={{ marginTop:3 }} color={C.text.highlight} weight='semibold'>${liquidity.toFixed()} {open_order_count} Orders</Text>
                    </View>
                </Button>
            </View>
            :<></>}
        </View>
    )
}

export default React.memo(AthleteMarket, (left, right) => {
    if(JSON.stringify(left) == JSON.stringify(right)){ return true }
    return false
})